import { Component, type FunctionComponent, memo, type ReactNode } from 'react'

import { useOpenChat, useTranslate } from '@orus.eu/pharaoh'

import { HardcodedFallbackCrashPage } from '../templates/hardcoded-fallback-crash-page.tsx'

import { GenericProblemMessage } from './generic-problem-message.tsx'

/**
 * CrashPage wrapped in an error boundary that falls back to HardcodedFallbackCrashPage
 * if the context-dependent CrashPageWithContext fails to render.
 *
 * This handles the case where an error is thrown during render and React's internal
 * state is inconsistent, causing context providers to be unavailable.
 */
export const CrashPage: FunctionComponent = memo(function CrashPage() {
  return (
    <CrashPageErrorBoundary>
      <CrashPageWithContext />
    </CrashPageErrorBoundary>
  )
})

/**
 * Error boundary that catches any errors from CrashPageWithContext
 * and falls back to HardcodedFallbackCrashPage.
 */
class CrashPageErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  override state = { hasError: false }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true }
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return <HardcodedFallbackCrashPage />
    }
    return this.props.children
  }
}

/**
 * The actual crash page implementation that uses context hooks.
 */
const CrashPageWithContext: FunctionComponent = memo(function CrashPageWithContext() {
  const translate = useTranslate()
  const openChat = useOpenChat()

  return (
    <GenericProblemMessage
      title={translate('problem_oops_title')}
      principalMessage={translate('problem_principal_message')}
      firstSubText={translate('problem_first_subtext')}
      secondSubText={translate('problem_second_subtext')}
      buttonText={translate('contact_customer_support')}
      onButtonClick={openChat}
      trackingId="contact_button"
    />
  )
})
